import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule, DatePipe, formatDate } from '@angular/common';
import { ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { HttpErrorResponse } from '@angular/common/http';

import { SampleManagerService } from '../../../services/sample-manager.service';
import { SampleManagerSearchResult } from '@models/sample-manager-search-result.interface';
import { SampleManagerSearchRequest } from '@models/sample-manager-search-request.interface';

@Component({
  selector: 'app-sample-manager-search-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatProgressSpinnerModule, MatPaginatorModule, DatePipe],
  templateUrl: './sample-manager-search-page.component.html',
  styleUrl: './sample-manager-search-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SampleManagerSearchPageComponent {
  private readonly sampleManagerService = inject(SampleManagerService);
  private readonly fb = inject(FormBuilder);

  protected isLoading = signal(false);
  protected results = signal<SampleManagerSearchResult[]>([]);
  protected totalCount = signal(0);
  protected errorMessage = signal<string | null>(null);
  protected hasSearched = signal(false);
  protected page = signal(1);
  protected pageSize = signal(100);
  protected lastRequest = signal<SampleManagerSearchRequest | null>(null);

  protected readonly pageSizeOptions = [25, 50, 100, 250, 500];
  protected readonly appliedFilters = computed(() => {
    const request = this.lastRequest();
    if (!request) return [] as Array<{ label: string; value: string }>;

    const filters: Array<{ label: string; value: string }> = [];

    if (request.materialName) filters.push({ label: 'Material Name', value: request.materialName });
    if (request.location) filters.push({ label: 'Location', value: request.location });
    if (request.notes) filters.push({ label: 'Notes', value: request.notes });
    if (request.receivedFrom) filters.push({ label: 'Received From', value: this.formatFilterDate(request.receivedFrom) });
    if (request.receivedTo) filters.push({ label: 'Received To', value: this.formatFilterDate(request.receivedTo) });
    filters.push({
      label: 'Owned Locations',
      value: request.showOwnedLocationsOnly ? 'Only owned' : 'All locations',
    });

    return filters;
  });

  protected form = this.fb.group({
    materialName: [''],
    location: [''],
    showOwnedLocationsOnly: [true],
    receivedFrom: [''],
    receivedTo: [''],
    notes: [''],
  });

  protected onSearch(): void {
    this.searchPage(1, this.pageSize());
  }

  protected onPageChange(event: PageEvent): void {
    this.searchPage(event.pageIndex + 1, event.pageSize);
  }

  private searchPage(page: number, pageSize: number): void {
    if (this.isLoading()) return;

    const v = this.form.value;
    const request: SampleManagerSearchRequest = {
      materialName: v.materialName?.trim() || null,
      location: v.location?.trim() || null,
      showOwnedLocationsOnly: v.showOwnedLocationsOnly ?? true,
      receivedFrom: v.receivedFrom || null,
      receivedTo: v.receivedTo || null,
      notes: v.notes?.trim() || null,
      page,
      pageSize,
    };

    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.sampleManagerService.search(request).subscribe({
      next: result => {
        this.results.set(result.items);
        this.totalCount.set(result.totalCount);
        this.page.set(result.pageIndex + 1);
        this.pageSize.set(result.pageSize);
        this.lastRequest.set(request);
        this.hasSearched.set(true);
        this.isLoading.set(false);
      },
      error: (err: HttpErrorResponse) => {
        this.errorMessage.set(
          err.error?.detail ?? err.message ?? 'An error occurred while contacting SampleManager.'
        );
        this.results.set([]);
        this.totalCount.set(0);
        this.page.set(page);
        this.pageSize.set(pageSize);
        this.lastRequest.set(request);
        this.hasSearched.set(true);
        this.isLoading.set(false);
      },
    });
  }

  protected formatDate(dateStr: string | null): string {
    return dateStr ? formatDate(dateStr, 'MM/dd/yyyy', 'en-US') : '';
  }

  protected formatLocation(location: string | null): string {
    if (!location) return '';

    const locationStart = location.indexOf('A-');
    return locationStart >= 0 ? location.substring(locationStart + 2) : location;
  }

  private formatFilterDate(isoDate: string): string {
    const d = new Date(`${isoDate}T00:00:00`);
    if (isNaN(d.getTime())) return isoDate;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }
}
